import { useCallback } from 'react';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { WORKORDER_ITEMS_QUERY_KEY } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useWorkorderComplementSyncWithPecaAi } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import {
  useWorkOrderPecaAiAddProductInBasket,
  WORKORDER_PECA_AI_BASKET_DETAIL,
} from '@/core/domain/workorder/pecaAi/workorder-pecaAi.hooks';
import { WorkOrderPecaAiSearchProductPossibilities } from '@/core/domain/workorder/pecaAi/workorder-pecaAi.types';
import { usePecaAiContext } from '../PecaAiProvider';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';

export default function usePecaAiProductActions() {
  const { addProduct, basketId, cnpj } = usePecaAiContext();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const addProductInBasket = useWorkOrderPecaAiAddProductInBasket();
  const syncWithPecaAi = useWorkorderComplementSyncWithPecaAi();
  const queryChanel = useQueryChanel();

  const addPossibility = useCallback(
    async (possibility: WorkOrderPecaAiSearchProductPossibilities) => {
      const amount = possibility.amount || 1;

      if (basketId && cnpj && workorder?.id && complement?.id) {
        await addProductInBasket.mutateAsync({
          amount,
          productId: possibility.id,
          basketId,
          document: cnpj,
        });

        await syncWithPecaAi.mutateAsync({
          workOrderId: workorder.id,
          complementId: complement.id,
        });

        queryChanel({
          invalidateQueries: [
            [WORKORDER_ITEMS_QUERY_KEY],
            [WORKORDER_PECA_AI_BASKET_DETAIL],
          ],
        });

        return;
      }

      addProduct({
        amount,
        description: possibility.description,
        brand: possibility.brand,
        inBasket: false,
        partNumber: possibility.partNumber,
        productId: possibility.id,
        totalPrice: possibility.productTotalPrice,
        unitPrice: possibility.productUnitPrice,
        shippingPrice: possibility.shippingPrice,
        deadlineHours: possibility.deadlineHours,
        image: possibility.image,
        observation:
          possibility.observation?.length >= 1
            ? possibility.observation[0].description
            : undefined,
      });
    },
    [
      addProduct,
      addProductInBasket,
      basketId,
      cnpj,
      complement?.id,
      queryChanel,
      syncWithPecaAi,
      workorder?.id,
    ],
  );

  return {
    addPossibility,
    isLoading: addProductInBasket.isLoading || syncWithPecaAi.isLoading,
  };
}
